import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import apiService from '@/services/api';
import { Pencil, Trash2, Plus } from 'lucide-react';

const Container = styled.div`
  padding: 24px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const InfoCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  margin: 0 0 16px 0;
  color: #334155;
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  color: #6b7280;
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const Button = styled.button`
  padding: 10px 12px;
  border-radius: 6px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const ButtonSmall = styled(Button)`
  padding: 8px 10px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
  th, td { border-bottom: 1px solid #e5e7eb; padding: 10px; text-align: left; }
  th { background: #f9fafb; font-weight: 600; font-size: 14px; }
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

type Recipient = { id: number; label: string | null; phone: string; active: 0 | 1 | boolean; created_at?: string; updated_at?: string };

const CallMeBotNumbers: React.FC = () => {
  const [form, setForm] = useState<{ label: string; phone: string; active: boolean }>({
    label: '',
    phone: '',
    active: true,
  });
  const [list, setList] = useState<Recipient[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const items = await apiService.listarCallMeBotRecipients();
        setList(items);
      } catch (e: any) {
        toast.error(e?.response?.data?.error || 'Falha ao carregar números');
      }
    })();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSalvar = async () => {
    try {
      if (editingId) {
        await apiService.atualizarCallMeBotRecipient(editingId, {
          label: form.label || undefined,
          phone: form.phone,
          active: form.active,
        });
      } else {
        await apiService.criarCallMeBotRecipient({
          label: form.label || undefined,
          phone: form.phone,
          active: form.active,
        });
      }
      toast.success('Número salvo');
      const items = await apiService.listarCallMeBotRecipients();
      setList(items);
      setEditingId(null);
      setForm({ label: '', phone: '', active: true });
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Falha ao salvar');
    }
  };

  const handleNovo = () => {
    setEditingId(null);
    setForm({ label: '', phone: '', active: true });
  };

  const handleEditar = async (id: number) => {
    const item = await apiService.obterCallMeBotRecipient(id);
    if (!item) return;
    setEditingId(id);
    setForm({ label: item.label || '', phone: item.phone, active: Boolean(item.active) });
  };

  const handleExcluir = async (id: number) => {
    if (!confirm('Confirma excluir este número?')) return;
    await apiService.excluirCallMeBotRecipient(id);
    toast.success('Número excluído');
    const items = await apiService.listarCallMeBotRecipients();
    setList(items);
  };

  return (
    <Container>
      <Card>
        <Title>Números que podem interagir com o whatsapp</Title>
        <InfoCard>
          Somente números cadastrados e ativos nesta tela podem solicitar relatórios pelo WhatsApp.
          Mensagens enviadas por outros números serão ignoradas.
        </InfoCard>
        <Actions>
          <Button onClick={handleNovo}><Plus size={16} /> Novo</Button>
        </Actions>
        <Table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Rótulo</th>
              <th>Telefone</th>
              <th>Ativo</th>
              <th>Atualizado</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {list.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.label || '-'}</td>
                <td>{item.phone}</td>
                <td>{Boolean(item.active) ? 'Sim' : 'Não'}</td>
                <td>{item.updated_at ? new Date(item.updated_at).toLocaleString('pt-BR') : '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <ButtonSmall onClick={() => handleEditar(item.id)}><Pencil size={16} /> Editar</ButtonSmall>
                    <ButtonSmall onClick={() => handleExcluir(item.id)}><Trash2 size={16} /> Excluir</ButtonSmall>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Grid>
          <Field>
            <Label>Rótulo</Label>
            <Input name="label" value={form.label} onChange={handleChange} placeholder="Opcional (ex.: Gerência)" />
          </Field>
          <Field>
            <Label>Telefone</Label>
            <Input name="phone" value={form.phone} onChange={handleChange} placeholder="ex.: +351968483466" />
          </Field>
          <CheckboxRow>
            <input name="active" type="checkbox" checked={form.active} onChange={handleChange} />
            Ativo
          </CheckboxRow>
        </Grid>
        <Actions>
          <Button onClick={handleSalvar}>Salvar</Button>
        </Actions>
      </Card>
    </Container>
  );
};

export default CallMeBotNumbers;


